/** @file timelineMenu: the right-click menu of a key-row cell (key, delete, copy / paste, ease, event). */
import { keyAt } from '@zilo/pose3d-commands/clip/keyAt';
import type { Ease } from '@zilo/pose3d-format/types/clip';
import type { EditorStore, MenuItem } from '../../state/editorTypes';
import { currentClip } from './currentClip';
import { deleteKeysCommand } from './deleteKeysCommand';
import { keyFrame } from './keyFrame';
import { mirrorGuard } from './mirrorGuard';
import { pastePose } from './pastePose';
import type { PoseClipboard } from './poseClipboard';

const EASES: Ease[] = ['linear', 'easeIn', 'easeOut', 'easeInOut', 'step'];
const SEP: MenuItem = { kind: 'separator' };

/** One item per ease, the key's current one ticked; disabled when the frame has no key. */
function easeItems(store: EditorStore, name: string, frame: number, current: Ease | undefined, hasKey: boolean): MenuItem[] {
  return EASES.map((ease) => ({
    kind: 'item', label: `Ease: ${ease}${current === ease ? ' (set)' : ''}`, disabled: !hasKey,
    onSelect: () => {
      void store.getState().dispatch([{ name: 'clip.setKeys', input: { clip: name, keys: [{ frame, nodes: {}, ease }], merge: 'mergeNodes' } }], `ease ${ease} @${frame}`);
    },
  }));
}

/** Appends a `step` event on `node` (if any) to the frame's events. */
function addEvent(store: EditorStore, frame: number, node: string | null): void {
  const s = store.getState();
  const cur = currentClip(s);
  if (cur === null || mirrorGuard(cur.clip, frame, s.notify)) return;
  const events = [...(keyAt(cur.clip, frame)?.events ?? []), node === null ? { type: 'step' as const } : { type: 'step' as const, node }];
  void s.dispatch([{ name: 'clip.setEvents', input: { clip: cur.name, frame, events } }], `add event @${frame}`);
}

/** Items for `node` (null = the summary row: the whole pose) at `frame` of the playhead clip; empty without a clip. */
export function timelineMenu(store: EditorStore, clipboard: PoseClipboard, node: string | null, frame: number): MenuItem[] {
  const cur = currentClip(store.getState());
  if (cur === null) return [];
  const key = keyAt(cur.clip, frame);
  const del = node === null ? null : deleteKeysCommand(cur.name, cur.clip, [{ node, frame }]);
  const nodes = node === null ? undefined : [node];
  return [
    { kind: 'item', label: node === null ? 'Key whole pose here' : `Key ${node} here`, chord: node === null ? 'Shift+KeyK' : 'KeyK', onSelect: () => { keyFrame(store, frame, nodes); } },
    { kind: 'item', label: 'Delete key', chord: 'Delete', disabled: del === null, onSelect: () => { if (del !== null) void store.getState().dispatch([del], 'delete key'); } },
    SEP,
    { kind: 'item', label: 'Copy pose from here', chord: 'Mod+KeyC', onSelect: () => { clipboard.current = { clip: cur.name, frame }; } },
    { kind: 'item', label: 'Paste pose here', chord: 'Mod+KeyV', disabled: clipboard.current === null, onSelect: () => { pastePose(store, clipboard, frame); } },
    SEP,
    ...easeItems(store, cur.name, frame, key?.ease, key !== undefined),
    SEP,
    { kind: 'item', label: 'Add event...', onSelect: () => { addEvent(store, frame, node); } },
  ];
}
